import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { Analytics } from '@vercel/analytics/react';
import { SpeedInsights } from '@vercel/speed-insights/react';
import { supabase } from './lib/supabaseClient';
import { DashboardLayout } from './layouts/DashboardLayout';
import { DownloadPage } from './pages/DownloadPage';
// import { VideoEditorPage } from './pages/VideoEditorPage'; // V2: Video editing features
import { HistoryPage } from './pages/HistoryPage';
import { SettingsPage } from './pages/SettingsPage';
import { LandingPage } from './pages/LandingPage';
import { SignInPage } from './pages/SignInPage';
import { SignUpPage } from './pages/SignUpPage';
import { TrendingPage } from './pages/TrendingPage';
import { TermsPage } from './pages/TermsPage';
import { PrivacyPage } from './pages/PrivacyPage';
import { VideoRankingPage } from './pages/VideoRankingPage';
import { RantCreatorPage } from './pages/RantCreatorPage';
import { AudioConverterPage } from './pages/AudioConverterPage';
import { VideoSplitterPage } from './pages/VideoSplitterPage';
import { ProjectsDashboardPage } from './pages/ProjectsDashboardPage';
import { QuizCreatorPage } from './pages/QuizCreatorPage';
import { VoiceClonePage } from './pages/VoiceClonePage';
import { ClipIntelligencePage } from './pages/ClipIntelligencePage';
import { YouTubePage } from './pages/YouTubePage';
import { FeaturesPage } from './pages/FeaturesPage';
import { AffiliatesPage } from './pages/AffiliatesPage';
import './App.css';

import { DotScreenShader } from './components/DotScreenShader';

const API_BASE = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5001';

const FullScreenLoader = ({ message }) => (
    <div style={{
        position: 'fixed',
        inset: 0,
        backgroundColor: '#0d0d0d',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 9999,
        color: '#fff'
    }}>
        <div style={{ position: 'fixed', inset: 0, zIndex: 0 }}>
            <DotScreenShader theme="dark" />
        </div>
        <div className="oauth-spinner" style={{ width: 40, height: 40, borderWidth: 3, marginBottom: 20, position: 'relative', zIndex: 1 }}></div>
        <p style={{ fontSize: '1.2rem', color: '#c3cfe0', fontFamily: 'inherit', position: 'relative', zIndex: 1 }}>{message}</p>
    </div>
);

function App() {
    const navigate = useNavigate();
    const [session, setSession] = useState(null);
    const pendingSessionId = useRef(null); // Fix race condition: capture session ID immediately
    const [profile, setProfile] = useState(null);
    const [tiers, setTiers] = useState(null);

    // Auth State
    // const [authView, setAuthView] = useState(null);
    const [authEmail, setAuthEmail] = useState('');
    const [authPassword, setAuthPassword] = useState('');
    const [authLoading, setAuthLoading] = useState(false);
    const [authError, setAuthError] = useState('');
    const [selectedSignupTier, setSelectedSignupTier] = useState('pro');
    // const [showSignupForm, setShowSignupForm] = useState(false);
    const [billingInterval, setBillingInterval] = useState('year');
    const [checkoutPending, setCheckoutPending] = useState(() => {
        if (typeof window === 'undefined') return false;
        const hash = window.location.hash || '';
        const search = window.location.search || '';
        // Check for Auth tokens (Implicit flow or PKCE)
        const hasAuthParams = hash.includes('access_token') || hash.includes('type=recovery') || search.includes('code=');
        // Check for pending tier
        const hasPendingTier = !!window.localStorage.getItem('cutiq_preferred_tier');

        const found = hasAuthParams && hasPendingTier;
        if (found) console.log('[Auth] Detected pending checkout from OAuth return');
        return found;
    });
    const [oAuthLoading, setOAuthLoading] = useState(false);

    const refreshUsage = useCallback(async () => {
        if (!session?.access_token) {
            setProfile(null);
            setTiers(null);
            return;
        }
        try {
            const res = await fetch(`${API_BASE}/api/me`, {
                headers: { Authorization: `Bearer ${session.access_token}` },
            });
            if (!res.ok) throw new Error('Failed to load account data');
            const data = await res.json();
            setProfile({ user: data.user, usage: data.usage });
            setTiers(data.tiers);
        } catch (err) {
            console.error(err);
        }
    }, [session]);

    useEffect(() => {
        if (!supabase) return;
        supabase.auth.getSession().then(({ data }) => {
            setSession(data.session);
        });
        const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, newSession) => {
            setSession(newSession);
        });
        return () => subscription.unsubscribe();
    }, []);

    useEffect(() => {
        refreshUsage();
    }, [refreshUsage]);

    // Stripe Redirect Handling
    // Fix: Capture session ID on mount regardless of auth state to prevent loss during redirect race condition
    const pendingPurchaseType = useRef(null); // Track if it's a credit purchase or subscription
    const checkoutLock = useRef(false);

    // New User "Sign In" Enforcement
    // If a brand new user (< 2 mins old) comes from "Sign In" (auth_intent='sign_in') and is 'free',
    // redirect them to /signup to choose a plan.
    useEffect(() => {
        if (profile?.usage && profile?.user) {
            const authIntent = window.localStorage.getItem('auth_intent');

            if (authIntent === 'sign_in' && profile.usage.subscription_tier === 'free') {
                const createdAt = new Date(profile.user.created_at).getTime();
                const now = Date.now();
                // If created within last 2 minutes
                if (now - createdAt < 120000) {
                    console.log('[Auth] New user detected from Sign In. Redirecting to Signup.');
                    // Clear intent so we don't loop forever if they navigate back
                    window.localStorage.removeItem('auth_intent');
                    navigate('/signup');
                }
            }
        }
    }, [profile, navigate]);

    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const sid = params.get('session_id');
        const purchaseType = params.get('type'); // 'credits' for credit packs, undefined for subscriptions
        const path = window.location.pathname;

        // Capture relevant session ID if on success page
        if (path.includes('/billing/success') && sid) {
            console.log('[BillingFix] Capturing session ID from URL:', sid, 'type:', purchaseType);
            pendingSessionId.current = sid;
            pendingPurchaseType.current = purchaseType; // 'credits' or null

            // Clean URL immediately to prevent re-processing
            window.history.replaceState({}, document.title, '/');
        } else if (path.includes('/billing/cancel')) {
            console.log('[Billing] Cancelled, cleaning URL');
            window.history.replaceState({}, document.title, '/');
            setCheckoutPending(false);
        }
    }, []); // Run once on mount

    // Process verify logic when session becomes available
    useEffect(() => {
        if (!session) return;

        const verifyPending = async () => {
            if (pendingSessionId.current) {
                const sessionId = pendingSessionId.current;
                const purchaseType = pendingPurchaseType.current;
                console.log('[BillingFix] Session active, verifying purchase:', sessionId, 'type:', purchaseType);
                pendingSessionId.current = null; // consume it
                pendingPurchaseType.current = null;

                try {
                    // Use correct endpoint based on purchase type
                    const endpoint = purchaseType === 'credits'
                        ? '/api/billing/verify-credit-purchase'
                        : '/api/billing/verify-session';

                    const res = await fetch(`${API_BASE}${endpoint}`, {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                            Authorization: `Bearer ${session.access_token}`,
                        },
                        body: JSON.stringify({ session_id: sessionId }),
                    });
                    const data = await res.json();
                    console.log('[BillingFix] Verification result:', data);
                    refreshUsage();
                } catch (e) {
                    console.error('[BillingFix] Verification failed:', e);
                }
                setCheckoutPending(false);
            }
        };

        verifyPending();
    }, [session, refreshUsage]);

    const startCheckout = async (tierKey, interval = 'month', tokenOverride = null) => {
        if (checkoutLock.current) return;

        const token = tokenOverride || session?.access_token;
        if (!token) return;

        checkoutLock.current = true;
        try {
            const res = await fetch(`${API_BASE}/api/billing/checkout`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify({ tier: tierKey, interval, referral_id: window.promotekit_referral }),
            });
            const data = await res.json();
            if (!res.ok) throw new Error(data.error || 'Unable to start checkout');
            window.location.assign(data.checkout_url);
        } catch (err) {
            checkoutLock.current = false;
            setAuthError(err.message);
            setAuthLoading(false);
            setCheckoutPending(false);
        }
    };

    const handleAuthSubmit = async (event, authType = 'sign_in') => {
        event.preventDefault();
        if (!supabase) return false;
        setAuthLoading(true);
        setAuthError('');

        try {
            const isSignUpFlow = authType === 'sign_up';
            if (isSignUpFlow) {
                const { data: signUpData, error: signUpError } = await supabase.auth.signUp({
                    email: authEmail,
                    password: authPassword,
                    options: {
                        data: {
                            full_name: authEmail.split('@')[0],
                        }
                    }
                });
                if (signUpError) throw signUpError;

                if (signUpData.session) {
                    if (selectedSignupTier !== 'free') {
                        // Track signup in PromoteKit (optional but recommended)
                        if (window.promotekit && typeof window.promotekit.refer === 'function') {
                            window.promotekit.refer(authEmail);
                        }

                        // Store preferences for potential recovery
                        try {
                            window.localStorage.setItem('cutiq_preferred_tier', selectedSignupTier);
                            window.localStorage.setItem('cutiq_preferred_interval', billingInterval);
                        } catch {
                            // ignore storage failures
                        }
                        // Set checkout pending to prevent dashboard from showing
                        setCheckoutPending(true);
                        // Keep loading state while redirecting to checkout
                        await startCheckout(selectedSignupTier, billingInterval, signUpData.session.access_token);
                        // Don't set loading to false - we're redirecting
                        return false;
                    } else {
                        // Track signup in PromoteKit (optional but recommended)
                        if (window.promotekit && typeof window.promotekit.refer === 'function') {
                            window.promotekit.refer(authEmail);
                        }

                        setAuthLoading(false);
                        setCheckoutPending(false);
                        return true;
                    }
                } else {
                    setAuthError('Please check your email to confirm your account.');
                    setAuthLoading(false);
                    setCheckoutPending(false);
                    return false;
                }
            } else {
                const { error: signInError } = await supabase.auth.signInWithPassword({
                    email: authEmail,
                    password: authPassword,
                });
                if (signInError) throw signInError;
                setAuthLoading(false);
                return true;
            }
        } catch (err) {
            setAuthError(err.message);
            setAuthLoading(false);
            setCheckoutPending(false);
            return false;
        }
    };

    const handleOAuth = async (isSignUpContext) => {
        if (!supabase) return;
        setOAuthLoading(true);

        // Check if explicitly called from signup flow (passed as true)
        const isSignUp = isSignUpContext === true;

        console.log('[Auth] handleOAuth called. Context:', isSignUpContext, 'Treating as SignUp:', isSignUp);

        // Store intent for post-login redirect logic
        try {
            window.localStorage.setItem('auth_intent', isSignUp ? 'sign_up' : 'sign_in');
        } catch (e) { }

        if (isSignUp) {
            // Save tier preference if signing up via OAuth
            if (selectedSignupTier && selectedSignupTier !== 'free') {
                try {
                    console.log('[Auth] Saving tier preference:', selectedSignupTier);
                    window.localStorage.setItem('cutiq_preferred_tier', selectedSignupTier);
                    window.localStorage.setItem('cutiq_preferred_interval', billingInterval);
                } catch (e) {
                    console.warn('Failed to save tier preference', e);
                }
            } else {
                // Explicitly clear preferences if free tier is selected
                console.log('[Auth] Clearing tier preference (Free selected)');
                try {
                    window.localStorage.removeItem('cutiq_preferred_tier');
                    window.localStorage.removeItem('cutiq_preferred_interval');
                } catch (e) { }
            }
        } else {
            // If Signing In (not Sign Up), ALWAYS clear any stale tier preferences
            console.log('[Auth] Clearing tier preference (Sign In context)');
            try {
                window.localStorage.removeItem('cutiq_preferred_tier');
                window.localStorage.removeItem('cutiq_preferred_interval');
            } catch (e) { }
        }

        try {
            const { error } = await supabase.auth.signInWithOAuth({
                provider: 'google',
                options: { redirectTo: window.location.origin },
            });
            if (error) throw error;
        } catch (err) {
            console.error('OAuth Error:', err);
            setOAuthLoading(false);
            setAuthError(err.message);
        }
    };

    // Check for pending tier selection after OAuth redirect
    useEffect(() => {
        if (session) {
            try {
                const pendingTier = window.localStorage.getItem('cutiq_preferred_tier');
                const pendingInterval = window.localStorage.getItem('cutiq_preferred_interval');

                if (pendingTier && pendingTier !== 'free') {
                    // Clear storage to prevent loops
                    window.localStorage.removeItem('cutiq_preferred_tier');
                    window.localStorage.removeItem('cutiq_preferred_interval');

                    console.log('[Auth] Found pending tier preference:', pendingTier);
                    setCheckoutPending(true);

                    // Trigger checkout
                    startCheckout(pendingTier, pendingInterval || 'month', session.access_token);
                }
            } catch (e) {
                console.error('Error processing pending checkout:', e);
            }
        }
    }, [session, checkoutPending]);

    const handleSignOut = async () => {
        if (!supabase) return;
        await supabase.auth.signOut();
        setProfile(null);
        setSession(null);
    };

    const renderLandingPage = (customProps = {}) => (
        <LandingPage {...customProps} />
    );

    // Unified Route Wrapper
    // If logged in -> Render Dashboard with custom DownloadPage props
    // If logged out -> Render LandingPage with custom props
    const UnifiedRoute = ({ customProps }) => {
        if (showLanding) {
            return renderLandingPage(customProps);
        }

        // When logged in, we render the DashboardLayout wrapping the DownloadPage
        // ideally we want to render the Layout, and pass the props down to the specific page.
        // Since Routes are nested, we can just render the layout here? 
        // No, the Layout expects an Outlet or children.
        // Actually, for specific routes like /youtubedownloader, we want to force the DownloadPage content 
        // but wrapped in the Dashboard shell.

        return (
            <DashboardLayout onSignOut={handleSignOut} profile={profile}>
                <DownloadPage
                    session={session}
                    profile={profile}
                    refreshUsage={refreshUsage}
                    {...customProps}
                />
            </DashboardLayout>
        );
    };

    // Determine if we should show landing page
    // Dev-only: allow viewing dashboard pages without auth for layout debugging.
    // Activate with `?dev_auth=1` on localhost; disabled in production builds.
    const devAuthBypass =
        process.env.NODE_ENV !== 'production' &&
        typeof window !== 'undefined' &&
        window.location?.hostname === 'localhost' &&
        new URLSearchParams(window.location.search).get('dev_auth') === '1';

    const showLanding = (!session || checkoutPending) && !devAuthBypass;

    return (
        <div className="App-Container">
            <Routes>
                {/* Public legal pages - accessible to everyone */}
                <Route path="/terms" element={<TermsPage />} />
                <Route path="/privacy" element={<PrivacyPage />} />
                <Route path="/features" element={<FeaturesPage />} />
                <Route path="/affiliates" element={<AffiliatesPage />} />

                {/* Auth Routes - Redirect to dashboard if already logged in */}
                <Route path="/signin" element={
                    session && !checkoutPending ? <Navigate to="/" replace /> :
                        <SignInPage
                            onAuthSubmit={handleAuthSubmit}
                            onOAuth={handleOAuth}
                            oAuthLoading={oAuthLoading}
                            authLoading={authLoading}
                            authError={authError}
                            authEmail={authEmail}
                            setAuthEmail={setAuthEmail}
                            authPassword={authPassword}
                            setAuthPassword={setAuthPassword}
                        />
                } />
                <Route path="/signup" element={
                    session && !checkoutPending ? <Navigate to="/" replace /> :
                        <SignUpPage
                            onAuthSubmit={handleAuthSubmit}
                            onOAuth={handleOAuth}
                            oAuthLoading={oAuthLoading}
                            authLoading={authLoading}
                            authError={authError}
                            authEmail={authEmail}
                            setAuthEmail={setAuthEmail}
                            authPassword={authPassword}
                            setAuthPassword={setAuthPassword}
                            selectedSignupTier={selectedSignupTier}
                            setSelectedSignupTier={setSelectedSignupTier}
                            billingInterval={billingInterval}
                            setBillingInterval={setBillingInterval}
                        />
                } />
                <Route path="/pricing" element={<Navigate to="/signup" replace />} />

                {/* SEO Routes - Accessible to all (Landing Page Style) */}
                {/* SEO Routes - Unified (Auth checking built-in) */}
                <Route path="/youtubedownloader" element={<UnifiedRoute customProps={{
                    customTitle: 'Youtube Downloader',
                    customSubtitle: 'Download Youtube Videos For Free',
                    customPlaceholderVideo: 'Paste a YouTube Link'
                }} />} />
                <Route path="/youtubeshortsdownloader" element={<UnifiedRoute customProps={{
                    customTitle: 'YT Shorts Downloader',
                    customSubtitle: 'Download Youtube Shorts For Free',
                    customPlaceholderVideo: 'Paste a YouTube Short Link'
                }} />} />
                <Route path="/tiktokdownloader" element={<UnifiedRoute customProps={{
                    customTitle: 'TikTok Downloader',
                    customSubtitle: 'Download TikTok Videos Without Watermark',
                    customPlaceholderVideo: 'Paste a TikTok Link'
                }} />} />
                <Route path="/instagramdownloader" element={<UnifiedRoute customProps={{
                    customTitle: 'Instagram Downloader',
                    customSubtitle: 'Download Instagram Reels, Stories & Videos',
                    customPlaceholderVideo: 'Paste an Instagram Link'
                }} />} />
                <Route path="/twitterdownloader" element={<UnifiedRoute customProps={{
                    customTitle: 'Twitter Downloader',
                    customSubtitle: 'Download Twitter (X) Videos & GIFs',
                    customPlaceholderVideo: 'Paste a Twitter Link'
                }} />} />
                <Route path="/facebookdownloader" element={<UnifiedRoute customProps={{
                    customTitle: 'Facebook Downloader',
                    customSubtitle: 'Download Facebook Videos',
                    customPlaceholderVideo: 'Paste a Facebook Link'
                }} />} />
                <Route path="/allvideodownloader" element={<UnifiedRoute customProps={{
                    customTitle: 'All Video Downloader',
                    customSubtitle: 'Download Videos From Any Platform',
                    customPlaceholderVideo: 'Paste a Video Link'
                }} />} />

                {/* Main app routes */}
                {checkoutPending ? (
                    <Route path="*" element={<FullScreenLoader message="Preparing checkout..." />} />
                ) : showLanding ? (
                    // Catch-all for landing page (generic)
                    <Route path="/*" element={renderLandingPage()} />
                ) : (
                    // Dashboard routes when logged in
                    <Route path="/" element={<DashboardLayout onSignOut={handleSignOut} profile={profile} />}>
                        <Route index element={<Navigate to="/smart-clips" replace />} />
                        {/* V2: Video editing features */}
                        {/* <Route path="editor" element={<VideoEditorPage session={session} profile={profile} />} /> */}
                        <Route path="trending" element={<TrendingPage />} />
                        <Route path="history" element={<HistoryPage session={session} />} />
                        <Route path="settings" element={<SettingsPage session={session} profile={profile} tiers={tiers} refreshUsage={refreshUsage} />} />
                        <Route path="ranking" element={<VideoRankingPage session={session} refreshUsage={refreshUsage} />} />
                        <Route path="rant-creator" element={<RantCreatorPage session={session} refreshUsage={refreshUsage} />} />
                        <Route path="audio-converter" element={<AudioConverterPage session={session} />} />
                        <Route path="video-splitter" element={<VideoSplitterPage session={session} />} />
                        <Route path="quiz-creator" element={<QuizCreatorPage session={session} refreshUsage={refreshUsage} />} />
                        <Route path="voice-clone" element={<VoiceClonePage session={session} refreshUsage={refreshUsage} />} />
                        <Route path="smart-clips" element={<ClipIntelligencePage session={session} refreshUsage={refreshUsage} />} />
                        <Route path="youtube" element={<YouTubePage session={session} profile={profile} />} />
                        <Route path="downloader" element={<DownloadPage session={session} profile={profile} refreshUsage={refreshUsage} />} />
                        <Route path="projects" element={<ProjectsDashboardPage session={session} />} />
                        <Route path="*" element={<Navigate to="/" replace />} />
                    </Route>
                )}
            </Routes>
            <Analytics />
            <SpeedInsights />
        </div>
    );
}

export default App;
